import React, { useMemo, useState } from 'react';
import { ClockIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { blogPosts } from '../blog/blogData';
import { BlogPost } from '../blog/types';
import BlogPostView from '../blog/BlogPostView';

const Blog: React.FC = () => {
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = useMemo(() => {
    const unique = Array.from(new Set(blogPosts.map((post) => post.category)));
    return ['All', ...unique];
  }, []);

  const filteredPosts = useMemo(() => {
    return blogPosts.filter((post) => {
      const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
      const search = searchQuery.trim().toLowerCase();
      const matchesSearch =
        search.length === 0 ||
        post.title.toLowerCase().includes(search) ||
        post.excerpt.toLowerCase().includes(search);
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  if (selectedPost) {
    return <BlogPostView post={selectedPost} onBack={() => setSelectedPost(null)} />;
  }

  return (
    <main className="min-h-screen text-text-main">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-20 md:py-28">
        
        {/* Header */}
        <header className="mb-16 space-y-4">
          <div className="inline-block">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary/80">Knowledge Base</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold leading-tight">
            Learn & <span className="text-primary">Master</span>
          </h1>
          <p className="text-xl text-text-muted max-w-2xl leading-relaxed">
            Comprehensive guides on PDF enhancement, privacy, and productivity tools for students and professionals.
          </p>
        </header>

        {/* Search and Filters */}
        <div className="mb-12 space-y-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-text-muted/60" />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search articles..."
              className="w-full rounded-2xl border border-border-glow/40 bg-glass/20 px-4 py-4 pl-12 text-base text-text-main placeholder:text-text-muted/60 backdrop-blur-sm transition-all focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-all ${
                  activeCategory === category
                    ? 'bg-primary/20 text-primary shadow-lg shadow-primary/10'
                    : 'bg-glass/20 text-text-muted backdrop-blur-sm hover:bg-glass/40 hover:text-text-main'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Blog Posts */}
        <section className="space-y-8">
          {filteredPosts.map((post) => {
            const Icon = post.icon;
            return (
              <article
                key={post.id}
                onClick={() => setSelectedPost(post)}
                className="group cursor-pointer rounded-3xl border border-border-glow/30 bg-glass/10 p-8 backdrop-blur-sm transition-all duration-300 hover:border-primary/40 hover:bg-glass/20 hover:shadow-2xl hover:shadow-primary/5"
              >
                <div className="space-y-5">
                  {/* Meta */}
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                      <Icon className="h-3.5 w-3.5" />
                      {post.category}
                    </span>
                    <span className="text-text-muted/80">{post.date}</span>
                    <span className="text-text-muted/40">•</span>
                    <span className="inline-flex items-center gap-1.5 text-text-muted/80">
                      <ClockIcon className="h-3.5 w-3.5" />
                      {post.readTime}
                    </span>
                  </div>

                  {/* Title */}
                  <h2 className="text-3xl font-bold leading-tight text-text-main transition-colors group-hover:text-primary">
                    {post.title}
                  </h2>

                  {/* Excerpt */}
                  <p className="text-lg leading-relaxed text-text-muted">
                    {post.excerpt}
                  </p>

                  {/* CTA */}
                  <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                    <span>Continue reading</span>
                    <svg className="h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </article>
            );
          })}
        </section>

        {/* Empty State */}
        {filteredPosts.length === 0 && (
          <div className="rounded-3xl border border-border-glow/30 bg-glass/10 px-8 py-16 text-center backdrop-blur-sm">
            <p className="text-lg text-text-muted">
              No articles found. Try adjusting your search or filters.
            </p>
          </div>
        )}
      </div>
    </main>
  );
};

export default Blog;
